import { HashtagSourcesApiResponse } from 'api/api.types';
import {
  Box,
  CaretRightSvg,
  color,
  Flex,
  Modal,
  PrimaryHeading,
  PrimaryText,
  spacing,
  StyleProps,
  typography,
  useColorMode,
  useDisclosure,
  useIsMobileView,
  useIsTabletView,
} from 'deepstash-ui';
import React, { useState } from 'react';
import { normalizeHashtagSources } from 'utils/normalizers/hashtag';
import { getRemWidthFromColumns } from 'utils/global';
import TopStashersSection from './sections/TopStashersSection';
import RelatedTopicsSection from './sections/RelatedTopicsSection';
import { Analytics, Events } from 'src/services/analytics';
import * as HashtagService from 'src/api/Hashtag';
import UsersModal from '../modals/users-modal/UsersModal';
import { UsersModalType } from 'types/enums';
import scrollbar from 'theme/Scrollbar.module.scss';
import Chip from '../chips/Chip';
import actionStrings from 'utils/strings/actionStrings';

interface TopicMetacontentProps {
  hashtagSourcesApiResponse: HashtagSourcesApiResponse;
}

const TopicMetacontent: React.FC<TopicMetacontentProps> = ({
  hashtagSourcesApiResponse,
}) => {
  const hashtagSources = normalizeHashtagSources(hashtagSourcesApiResponse);
  const isTabletView = useIsTabletView();
  const { colorMode } = useColorMode();
  const isMobileView = useIsMobileView();

  const {
    isOpen: isOpenUsersModal,
    onOpen: onOpenUsersModal,
    onClose: onCloseUsersModal,
  } = useDisclosure();
  const {
    isOpen: isOpenTopicsModal,
    onOpen: onOpenTopicsModal,
    onClose: onCloseTopicsModal,
  } = useDisclosure();

  const topicName = hashtagSources.hashtag;
  const { topStashers, relatedTopics } = hashtagSources;
  const [allRelatedTopics, setAllRelatedTopics] =
    useState<string[]>(relatedTopics);

  const onTopStashersClick = () => {
    onOpenUsersModal();
    Analytics.logEvent({
      eventName: Events.consumption.selectTopStashers,
      properties: {
        hashtagName: topicName,
      },
      platforms: ['amplitude'],
    });
  };

  const renderTopStashersButton = () => (
    <Flex
      onClick={onTopStashersClick}
      color={color[colorMode].primary.default}
      cursor="pointer"
      _hover={{
        textDecoration: 'underline',
      }}
      alignItems="center"
    >
      <PrimaryText {...typography.primaryTextFonts.small.bold}>
        {actionStrings.seeAllStashers}
      </PrimaryText>
      <CaretRightSvg boxSize="1rem" color={color[colorMode].primary.default} />
    </Flex>
  );

  const loadAllTopics = () =>
    HashtagService.getRelatedTopics(topicName)?.then(response =>
      setAllRelatedTopics(response.objects.map(item => item.name)),
    );

  const onSeeTopicsClick = () => {
    onOpenTopicsModal();
    loadAllTopics();
    Analytics.logEvent({
      eventName: Events.consumption.selectRelatedTopics,
      properties: {
        hashtagName: topicName,
      },
      platforms: ['amplitude'],
    });
  };

  const renderAllTopicsButton = () => (
    <Flex
      onClick={onSeeTopicsClick}
      color={color[colorMode].primary.default}
      cursor="pointer"
      _hover={{
        textDecoration: 'underline',
      }}
    >
      <PrimaryText {...typography.primaryTextFonts.small.bold}>
        {actionStrings.seeAllTopics}
      </PrimaryText>
      <CaretRightSvg
        boxSize="1rem"
        color={color[colorMode].primary.default}
        marginTop={spacing.XXS.rem}
      />
    </Flex>
  );

  const renderTopStashersSection = (props?: StyleProps) => (
    <TopStashersSection
      topStashers={topStashers}
      rightHeaderElement={renderTopStashersButton()}
      {...props}
    />
  );

  const renderRelatedTopicsSection = (props?: StyleProps) => (
    <RelatedTopicsSection
      hashtags={hashtagSources.relatedTopics.map(hashtag => ({
        name: hashtag,
      }))}
      rightHeaderElement={renderAllTopicsButton()}
      {...props}
    />
  );

  const usersModal = isOpenUsersModal ? (
    <UsersModal
      isOpen={isOpenUsersModal}
      title={`Top stashers in #${hashtagSources.hashtag}`}
      onClose={onCloseUsersModal}
      data={hashtagSources.topStashers}
      id={hashtagSources.hashtag}
      usecase={UsersModalType.TOP_STASHERS}
    />
  ) : null;

  const topicsModal = isOpenTopicsModal ? (
    <Modal
      isOpen={isOpenTopicsModal}
      onClose={onCloseTopicsModal}
      borderRadius="1rem"
      backgroundColor={color[colorMode].surface}
      height="60vh"
      maxWidth="28rem"
      width={{ base: 'calc(100% - 1rem)', lg: '40vw' }}
      modalBodyProps={{
        className:
          colorMode === 'light'
            ? scrollbar.scrollVerticalLightSurface
            : scrollbar.scrollVerticalDarkSurface,
      }}
    >
      <Flex
        color={color[colorMode].text}
        width="100%"
        justifyContent="flex-start"
      >
        <PrimaryHeading
          size="h2"
          textSize={isMobileView ? 's' : 'm'}
          type="bold"
        >
          {`Related topics in #${hashtagSources.hashtag}`}
        </PrimaryHeading>
      </Flex>
      <Flex marginTop="1rem" flexWrap="wrap" gridGap={spacing.XS.rem}>
        {allRelatedTopics.map(topic => (
          <Box
            key={`related-topics-modal-item-${topic}`}
            onClick={onCloseTopicsModal}
          >
            <Chip content={`#${topic}`} href={`/topic/${topic}`} m="unset" />
          </Box>
        ))}
      </Flex>
    </Modal>
  ) : null;

  if (isTabletView) {
    return (
      <Flex width="100%" gridGap={spacing.XXXL.rem} mb={spacing.XXXL.rem}>
        {topStashers?.length > 0 &&
          renderTopStashersSection({ width: getRemWidthFromColumns(4) })}
        {relatedTopics?.length > 0 &&
          renderRelatedTopicsSection({
            width: getRemWidthFromColumns(4),
          })}
        {usersModal}
        {topicsModal}
      </Flex>
    );
  }

  if (isMobileView) {
    return (
      <Flex
        flexDirection="column"
        width="100%"
        gridGap={spacing.XXXL.rem}
        mb={spacing.XXXL.rem}
      >
        {topStashers?.length > 0 && renderTopStashersSection()}
        {relatedTopics?.length > 0 && renderRelatedTopicsSection()}
        {usersModal}
        {topicsModal}
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" width="100%" gridGap={spacing.M.rem}>
      <Flex flexDirection="column" width="100%" gridGap={spacing.XXXL.rem}>
        {topStashers?.length > 0 && renderTopStashersSection({ width: '100%' })}
        {relatedTopics?.length > 0 &&
          renderRelatedTopicsSection({ width: '100%' })}
      </Flex>
      {usersModal}
      {topicsModal}
    </Flex>
  );
};

export default TopicMetacontent;
